import React, { useState, useEffect } from 'react';
import './RecommendedProducts.css';
import ReactGA from 'react-ga4';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

const RecommendedProducts = ({ title = "Recommended Sleep Products", subtitle }) => {
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loadedImages, setLoadedImages] = useState({});

  // All available products
  const allProducts = [
    {
      id: 1,
      name: "CuboAi Sleep Safety Bundle",
      shortDescription: "Advanced baby monitor with breathing detection and safety alerts to give parents peace of mind during sleep time.",
      image: "/images/product-cubo-ai.jpg",
      link: "https://amzn.to/3SuT3aJ"
    },
    {
      id: 2,
      name: "Love to Dream Swaddle UP",
      shortDescription: "Self-soothing swaddle that allows babies to sleep in their natural arms-up position, reducing startle reflex for better sleep.",
      image: "/images/product-sleeping-bag.jpg",
      link: "https://amzn.to/432CAzr"
    },
    {
      id: 3,
      name: "Dreamegg White Noise Machine",
      shortDescription: "Portable sound machine with 19 soothing sounds to create a consistent sleep environment for your baby at home or on the go.",
      image: "/images/product-white-noise.jpg",
      link: "https://amzn.to/4k7wsNt"
    },
    {
      id: 4,
      name: "HARPPA Cuddly Bassinet",
      shortDescription: "Adjustable 3-in-1 bassinet with wheels for keeping baby close and secure during sleep time.",
      image: "/images/product-cuddly-basssinet.jpg",
      link: "https://amzn.to/3Stg3XC"
    },
    {
      id: 5,
      name: "Organic Bassinet Sheets",
      shortDescription: "100% cotton sheets that provide a soft, breathable sleep surface for sensitive baby skin.",
      image: "/images/product-bassinet-sheet.jpg",
      link: "https://www.amazon.com.au/2-Pack-Organic-Bassinet-Sheet-Girls/dp/B0CCV265SZ?linkCode=sl1&tag=dozzy-22&linkId=1544bf0cea6b745e44fc67bc55f2bc46&language=en_AU&ref_=as_li_ss_tl"
    },
    {
      id: 6,
      name: "Babylo Folding Travel Crib",
      shortDescription: "Easy-to-fold portable crib that provides a consistent sleep space for your baby when traveling.",
      image: "/images/product-crib.jpg",
      link: "https://amzn.to/42ZVyXy"
    },
    {
      id: 7,
      name: "3-in-1 Co-Sleeper Bassinet",
      shortDescription: "Adjustable height bassinet with mesh walls for visibility and airflow, keeping baby close for nighttime feedings.",
      image: "/images/product-bassinet.jpg",
      link: "https://amzn.to/4d25HaO"
    },
    {
      id: 8,
      name: "Tommee Tippee Baby Care Kit",
      shortDescription: "Complete healthcare kit with essential tools to keep baby comfortable for better sleep.",
      image: "/images/product-grooming-kit.jpg",
      link: "https://amzn.to/43isYSv"
    },
    {
      id: 9,
      name: "Baby Food Bible",
      shortDescription: "Guide to proper nutrition that helps establish healthy eating patterns for better sleep quality.",
      image: "/images/product-baby-bible.jpg",
      link: "https://amzn.to/4lVgYh8"
    }
  ];

  // State for rotated products
  const [products, setProducts] = useState([]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Product rotation logic
  useEffect(() => {
    // Shuffle the products array
    const shuffleArray = (array) => {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    // Get a random selection of products (8 products)
    // If we have fewer than 8 products, duplicate some to reach 8
    let rotatedProducts = shuffleArray(allProducts);
    if (rotatedProducts.length < 8) {
      // Duplicate products as needed to reach 8
      while (rotatedProducts.length < 8) {
        // Get a random product from the shuffled array
        const randomIndex = Math.floor(Math.random() * allProducts.length);
        const duplicateProduct = { 
          ...allProducts[randomIndex],
          id: `${allProducts[randomIndex].id}-duplicate-${rotatedProducts.length}` // Ensure unique ID
        };
        rotatedProducts.push(duplicateProduct);
      }
      // Shuffle again to mix the duplicates
      rotatedProducts = shuffleArray(rotatedProducts);
    }
    
    // Take exactly 8 products
    rotatedProducts = rotatedProducts.slice(0, 8);
    setProducts(rotatedProducts);
    
    // Set loading to false after a short delay to simulate loading
    setTimeout(() => {
      setLoading(false);
    }, 800);
  }, []);

  // Handle image load
  const handleImageLoad = (productId) => {
    setLoadedImages(prev => ({
      ...prev,
      [productId]: true
    }));
  };

  // Track product click
  const handleProductClick = (product) => {
    ReactGA.event({
      category: 'Product',
      action: 'Click',
      label: product.name
    });
  };

  // Render product card
  const renderProductCard = (product) => {
    const isImageLoaded = loadedImages[product.id];
    
    return (
      <div key={product.id} className="product-card">
        <div className="product-image-container">
          {!isImageLoaded && <div className="product-image-loading"></div>}
          <img 
            src={product.image} 
            alt={product.name} 
            className={`product-image ${isImageLoaded ? 'loaded' : ''}`}
            onLoad={() => handleImageLoad(product.id)}
          />
        </div>
        <div className="product-info">
          <h3 className="product-name">{product.name}</h3>
          <p className="product-description">{product.shortDescription}</p>
          <a 
            href={product.link} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="product-link-button"
            onClick={() => handleProductClick(product)}
          >
            View on Amazon
          </a>
        </div>
      </div>
    );
  };

  // Render mobile carousel
  const renderMobileCarousel = () => {
    return (
      <div className="product-carousel-container">
        <Carousel 
          showArrows={true}
          showStatus={false}
          showThumbs={false}
          infiniteLoop={true}
          centerMode={false}
          emulateTouch={true}
          swipeable={true}
          autoPlay={false}
          className="product-carousel"
        >
          {products.map(product => (
            <div key={product.id} className="carousel-slide">
              {renderProductCard(product)}
            </div>
          ))}
        </Carousel>
      </div>
    );
  };

  // Render desktop grid
  const renderDesktopGrid = () => {
    return (
      <div className="recommended-products-grid">
        {products.map(product => renderProductCard(product))}
      </div>
    );
  };

  return (
    <div className="recommended-products-container">
      <div className="recommended-products-header">
        <h2>{title}</h2>
        {subtitle && <p className="recommended-products-subtitle">{subtitle}</p>}
        
      </div>
      
      {loading ? (
        <div className="products-loading-container">
          <div className="products-loading-spinner"></div>
          <p>Loading recommended products...</p>
        </div>
      ) : (
        isMobile ? renderMobileCarousel() : renderDesktopGrid()
      )}
      
        <p className="recommended-products-disclaimer">
          As Amazon Associates, we earn from qualifying purchases. Products selected based on quality and parent reviews.
        </p>
    </div>
  );
};

export default RecommendedProducts;
